import {TooltipProps} from "@fluentui/react-components";
import {ErrorResponse} from "../io/Response";

export type StringKeyOf<T> = Extract<keyof T, string>

export type TooltipContent<T> = Partial<Record<keyof T, TooltipProps['content']>>

export namespace Errors {
    export async function stringify(e: Error, depth: number = 0): Promise<string> {
        let s: string = ''
        if (depth === 0) {
            s = `user-agent: ${navigator.userAgent}\n`
        }
        if (e instanceof ErrorResponse) {
            s += `name: ${e.name}\ntypeName: ${e.typeName}\nmessage: ${e.message}\ncall stack: ${e.stack}`
        } else {
            s += `name: ${e.name}\nmessage: ${e.message}\ncall stack: ${e.stack}`
        }

        if (e.cause !== undefined) {
            if (e.cause instanceof Error) {
                s += "\ncaused by\n" + await stringify(e.cause, depth + 1)
            } else {
                s += "\ncaused by\n" + String(e)
            }
        }

        return s
    }
}

export namespace Types {
    export function asNull<T, R>(o: T | null | undefined, transform: (o: T) => R): R | null {
        if (o === null || o === undefined) {
            return null
        } else {
            return transform(o)
        }
    }

    export function asUndefined<T, R>(o: T | null | undefined, transform: (o: T) => R): R | undefined {
        if (o === null || o === undefined) {
            return undefined
        } else {
            return transform(o)
        }
    }

    export function present(o: any, ifNull: string = '-'): string {
        if (o === null) {
            return ifNull
        }
        if (typeof o === 'string') {
            return o
        }
        if (typeof o === 'number') {
            return String(o)
        }
        if (typeof o === 'bigint') {
            return String(o)
        }
        if (typeof o === 'boolean') {
            return o ? 'O' : 'X'
        }
        if (o instanceof Date) {
            return o.toISOString()
        }

        throw new Error('Unexpected type "' + o + '"')
    }

    export function nullIfEmpty<R>(o: string, transform: (o: string) => R): R | null {
        if (o.length === 0) {
            return null
        } else {
            return transform(o)
        }
    }
}

export type Identifiable = {
    id: bigint
}

export type DataTransferObjectValidation<DTO> = {
    [P in keyof DTO]: (o: string) => DTO[P] | Error
}

export namespace Validation {
    export function bigint(o: string, errorMessage: string): bigint | Error {
        try {
            return BigInt(o)
        } catch (error) {
            return new Error(errorMessage, { cause: error })
        }
    }

    export function date(o: string, errorMessage: string): Date | Error {
        try {
            const date = new Date(o)
            if (date.toISOString() === o) {
                return date
            } else {
                return new Error(errorMessage)
            }
        } catch (error) {
            return new Error(errorMessage, { cause: error })
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export namespace DataTransferObjectValidation {
    export function validate<DTO, K extends keyof DTO>(
        validation: DataTransferObjectValidation<DTO>,
        key: K,
        o: string,
        onError: (error: Error) => void
    ): DTO[K] {
        const result = validation[key](o)
        if (result instanceof Error) {
            onError(result)

            throw result
        }

        return result
    }

    export function validateNumber(o: string, what: string): string | Error {
        if (isNaN(Number(o))) {
            return new Error(what)
        } else {
            return o
        }
    }

    export function validateNumberOrNullIfEmpty(o: string, what: string): string | null | Error {
        if (o.length === 0) {
            return null
        }

        return validateNumber(o, what)
    }

    export function validateBigInt(o: string, what: string): string | Error {
        try {
            BigInt(o)

            return o
        } catch (e) {
            return new Error(what, { cause: e })
        }
    }

    export function validateBigIntOrNullIfEmpty(o: string, what: string): string | null | Error {
        if (o.length === 0) {
            return null
        }

        return validateBigInt(o, what)
    }

    export function validateBoolean(o: string, what: string): string | Error {
        if (o === 'true') {
            return o
        } else if (o === 'false') {
            return o
        } else {
            return new Error(what)
        }
    }

    export function validateBooleanOrNullIfEmpty(o: string, what: string): string | null | Error {
        if (o.length === 0) {
            return null
        }

        return validateBoolean(o, what)
    }

    export function validateDate(o: string, what: string): string | Error {
        try {
            const date = new Date(o)
            if (date.toISOString().substring(0, 10) === o) {
                return o
            } else {
                return new Error(what)
            }
        } catch (e) {
            return new Error(what, { cause: e })
        }
    }

    export function validateDateOrNullIfEmpty(o: string, what: string): string | null | Error {
        if (o.length === 0) {
            return null
        }

        return validateDate(o, what)
    }

    export function validateTime(o: string, what: string): string | Error {
        try {
            const date = new Date('1970-01-01T' + o + 'Z')
            if (date.toISOString().substring(11, 11 + o.length) === o) {
                return o
            } else {
                return new Error(what)
            }
        } catch (e) {
            return new Error(what, { cause: e })
        }
    }

    export function validateTimeOrNullIfEmpty(o: string, what: string): string | null | Error {
        if (o.length === 0) {
            return null
        }

        return validateTime(o, what)
    }

    export function validateStringOrNullIfEmpty(o: string, what: string): string | null {
        if (o.length === 0) {
            return null
        }

        return o
    }

    export function transformNumber(o: string, what: string): number | Error {
        const result = Number(o)
        if (isNaN(result)) {
            return new Error(what)
        } else {
            return result
        }
    }

    export function transformNumberOrNullIfEmpty(o: string, what: string): number | null | Error {
        if (o.length === 0) {
            return null
        }

        return transformNumber(o, what)
    }

    export function transformBigInt(o: string, what: string): bigint | Error {
        try {
            return BigInt(o)
        } catch (e) {
            return new Error(what, { cause: e })
        }
    }

    export function transformBigIntOrNullIfEmpty(o: string, what: string): bigint | null | Error {
        if (o.length === 0) {
            return null
        }

        return transformBigInt(o, what)
    }

    export function transformBoolean(o: string, what: string): boolean | Error {
        if (o === 'true') {
            return true
        } else if (o === 'false') {
            return false
        } else {
            return new Error(what)
        }
    }

    export function transformBooleanOrNullIfEmpty(o: string, what: string): boolean | null | Error {
        if (o.length === 0) {
            return null
        }

        return transformBoolean(o, what)
    }

    export function transformDate(o: string, what: string): Date | Error {
        try {
            const date = new Date(o)
            if (date.toISOString().substring(0, 10) === o) {
                return date
            } else {
                return new Error(what)
            }
        } catch (e) {
            return new Error(what, { cause: e })
        }
    }

    export function transformDateOrNullIfEmpty(o: string, what: string): Date | null | Error {
        if (o.length === 0) {
            return null
        }

        return transformDate(o, what)
    }
}

export function numberRange(from: number, to: number): Iterable<number> {
    return range(from, to, value => value + 1, (o1, o2) => o1 < o2)
}

export function bigintRange(from: bigint, to: bigint): Iterable<bigint> {
    return range(from, to, value => value + 1n, (o1, o2) => o1 < o2)
}

export function range<T>(from: T, to: T, inc: (value: T) => T, cmp: (o1: T, o2: T) => boolean): Iterable<T> {
    let i = from
    return {
        [Symbol.iterator](): Iterator<T> {
            return {
                next(): IteratorResult<T> {
                    const next = i
                    i = inc(i)
                    return {
                        value: next,
                        done: cmp(i, to)
                    }
                }
            }
        }
    }
}

export namespace BigIntegers {
    export function max(...values: bigint[]): bigint {
        if (values.length === 0) {
            throw new Error('values.length === 0')
        }

        let max = values[0]
        for (let i = 1; i < values.length; i++) {
            if (max < values[i]) {
                max = values[i]
            }
        }

        return max
    }

    export function min(...values: bigint[]): bigint {
        if (values.length === 0) {
            throw new Error('values.length === 0')
        }

        let min = values[0]
        for (let i = 1; i < values.length; i++) {
            if (min > values[i]) {
                min = values[i]
            }
        }
        console.log('min', min, 'values', values)

        return min
    }
}